import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Api } from '../../services/api';
import { LoaderService } from '../../services/loader';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Toaster } from '../../services/toaster';
import { fadeInAnimation } from '../../animation';
import { Subscription } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';
import { TableModule } from 'primeng/table';
import { Common } from '../../services/common';

@Component({
  selector: 'app-chat-history-supervisor',
  imports: [CommonModule, TooltipModule, MarkdownModule, TableModule],
  templateUrl: './chat-history-supervisor.html',
  providers: [DecimalPipe],
  styleUrl: './chat-history-supervisor.scss',
  animations: [fadeInAnimation]
})
export class ChatHistorySupervisor implements OnInit, OnChanges, OnDestroy {
  @Input() issueIds: string = '';
  @Input() selectedConversationId: string = '';
  @Input() pflmId: number | null = 0;
  @Output() dataLoaded = new EventEmitter<void>();
  @Input() reasonsData: any[] = [];
  @Input() impactAreaData: any[] = [];
  @Input() severityData: any[] = [];
  showContent = true;
  messages = [] as any;
  // userProfilePic = 'assets/images/user-chat-icon.png';
  userProfilePic = 'assets/images/user-chat-icon.png';
  botProfilePic = 'assets/images/chatbot-icon.svg';
  private subscription: Subscription = new Subscription();
  summarizationData: any[] = [];
  summarizationTimestamp: string = '';
  isSummarizationLoaded: boolean = false;
  updatedConversationIds: string[] = [];

  constructor(private apiService: Api, private loaderService: LoaderService,
    private toasterService: Toaster, private decimalPipe: DecimalPipe,
    public common: Common
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.apiService.updatedConversationIds$.subscribe((id) => {
        this.updatedConversationIds = id;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedConversationId']) {
      this.summarizationData = [];
      this.isSummarizationLoaded = false;
      this.summarizationTimestamp = '';
      setTimeout(() => {
        this.fetchChatHistory();
        this.fetchSummarization();
      }, 0);
    }
  }

  private fetchChatHistory(): void {
    this.loaderService.show();

    const conversationId = this.selectedConversationId?.trim();
    if (!conversationId) {
      this.messages = [];
      return;
    }

    const payload = { "ConversationId": conversationId };

    // 🔥 FIX #1: use add() instead of overwriting subscription
    this.subscription.add(
      this.apiService.fetchChatHistoryAPI(payload).subscribe({
        next: (res: any) => {
          this.loaderService.hide();
          // Response can be a single object or an array of objects
          const items: any[] = Array.isArray(res) ? res : [res];
          const mergedMessages: any[] = [];

          items.forEach(item => {
            const chat = Array.isArray(item?.ChatHistoryConversation)
              ? item.ChatHistoryConversation
              : [];

            chat.forEach((turn: any) => {
              const role = (turn?.role || '').toLowerCase();
              mergedMessages.push({
                Sender: role === 'assistant' ? 'bot' : 'user',
                Text: String(turn?.content ?? ''),
                Timestamp: turn?.timestamp ?? null
              });
            });
          });

          this.messages = mergedMessages;
          this.dataLoaded.emit();

          if (this.messages.length === 0) {
            this.toasterService.setError({
              message: 'No chat history available.',
              type: 'info'
            });
          }
        },
        error: (err: any) => {
          this.loaderService.hide();
          this.messages = [];
          this.toasterService.setError({
            message: 'Unable to fetch the data.',
            type: 'error'
          });
        },

        complete: () => {
          this.loaderService.hide();
        }
      })
    );
  }

  /**
   * Separate standalone method to handle chat summarization side effects
   */
  private fetchSummarization(): void {
    // Using your temporary hardcoded payload
    const payloadData = {
      process_flm_loc_map_id: this.pflmId,
      update_conversation_ids: this.updatedConversationIds
    };

    this.subscription.add(
      this.apiService.getSummarization(payloadData).subscribe({
        next: (res: any) => {
         if (res) {
          if (Array.isArray(res)) {
            // Direct array format (Matches your console log)
            this.summarizationData = [...res];
          } else if (typeof res === 'object') {
            // Dictionary/Map format fallback
            this.summarizationData = Object.values(res).flat();
          } else {
            this.summarizationData = [];
          }
        } else {
          this.summarizationData = [];
        }

        // Handle timestamps safely
        if (this.summarizationData.length > 0) {
          const firstRow = this.summarizationData[0];
          this.summarizationTimestamp = firstRow?.updated_date || firstRow?.created_date || new Date().toISOString();
        } else {
          this.summarizationTimestamp = new Date().toISOString();
        }

        // 🔥 IMPORTANT: This flag MUST be set to true so the *ngIf allows the HTML to display
        this.isSummarizationLoaded = true;
        },
        error: (err: any) => {
          console.error('[ChatHistorySupervisor] Summarization Error:', err);
          this.summarizationData = [];
          this.summarizationTimestamp = new Date().toISOString();
          this.isSummarizationLoaded = true;
        }
      })
    );
  }

  // Very basic date formatter (just returns the string for now)
  convertDate(dateString: string): string {
    return dateString;
  }

  formatChatHistoryDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);

    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // AM/PM logic
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12

    const hh = String(hours).padStart(2, '0');

    return `${mm}/${dd}/${yyyy} ${hh}:${minutes}:${seconds} ${ampm}`;
  }

  formatNumbersInString(value: any): string {
    const formatIfNeeded = (match: string, dollar: string, rawNumber: string) => {
      const numeric = rawNumber.replace(/,/g, '');
      const parsed = Number(numeric);

      if (isNaN(parsed)) return match;

      // Detect original decimal precision
      const hasDecimal = rawNumber.includes('.');
      const decimalPlaces = hasDecimal ? (rawNumber.split('.')[1]?.length || 0) : 0;

      const format = `1.${decimalPlaces}-${decimalPlaces}`;
      const formatted = parsed >= 1000
        ? this.decimalPipe.transform(parsed, format)
        : parsed.toString();

      return (dollar ?? '') + (formatted ?? match);
    };

    if (typeof value === 'number' || this.apiService.isNumeric(value)) {
      const valueStr = value.toString();
      const hasDecimal = valueStr.includes('.');
      const decimalPlaces = hasDecimal ? (valueStr.split('.')[1]?.length || 0) : 0;
      const format = `1.${decimalPlaces}-${decimalPlaces}`;

      return Number(value) >= 1000
        ? this.decimalPipe.transform(Number(value), format) ?? valueStr
        : valueStr;
    }

    if (typeof value === 'string') {
      return value.replace(/\b(\$)?([\d,]+(?:\.\d+)?)\b/g, formatIfNeeded);
    }

    return String(value ?? '');
  }

  /**
   * Helper mapping methods to resolve raw IDs to master data text values
   */
  getReasonText(reasonId: any): string {
    if (!reasonId || !this.reasonsData) return String(reasonId ?? '');
    const found = this.reasonsData.find(item => Number(item.id) === Number(reasonId));
    return found ? found.reason : String(reasonId);
  }

  getImpactAreaText(impactId: any): string {
    if (!impactId || !this.impactAreaData) return String(impactId ?? '');
    const found = this.impactAreaData.find(item => Number(item.impact_id) === Number(impactId));
    return found ? found.impact : String(impactId);
  }

  getSeverityText(severityId: any): string {
    if (!severityId || !this.severityData) return String(severityId ?? '');
    const found = this.severityData.find(item => Number(item.severity_id) === Number(severityId));
    return found ? found.severity : String(severityId);
  }

  closeChatBox(): void {
    this.messages = [];
    this.apiService.showChatHistory$.next(false);
    this.apiService.chatHistoryIssueIds$.next('');
  }
}
